import React from 'react';
import { Avatar } from '@mui/material';
import { PlanTaskOwner } from '../types/today';

interface TaskAvatarProps {
  owner: PlanTaskOwner;
  size?: number;
}

/**
 * Simple MD5 hash implementation for Gravatar URLs
 * Based on the MD5 algorithm - simplified for browser use
 */
function simpleMD5(str: string): string {
  // For production, use a proper MD5 library like 'blueimp-md5' or 'crypto-js'
  // This is a fallback that creates a consistent hash-like string
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  // Convert to hex-like string (32 chars)
  const hashStr = Math.abs(hash).toString(16).padStart(8, '0');
  return hashStr.repeat(4).substring(0, 32);
}

/**
 * Get Gravatar URL from email
 */
function getGravatarUrl(email: string, size: number = 64): string {
  const hash = simpleMD5(email.trim().toLowerCase());
  return `https://www.gravatar.com/avatar/${hash}?s=${size}&d=identicon`;
}

/**
 * Get initials from name
 */
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

/**
 * Generate consistent color from name
 */
function getColorFromName(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash % 360);
  return `hsl(${hue}, 65%, 50%)`;
}

/**
 * Avatar component with Gravatar support
 */
export const TaskAvatar: React.FC<TaskAvatarProps> = ({ owner, size = 28 }) => {
  const avatarUrl = owner.avatarUrl || (owner.email ? getGravatarUrl(owner.email, size) : undefined);

  if (avatarUrl) {
    return (
      <Avatar
        src={avatarUrl}
        alt={owner.name}
        sx={{
          width: size,
          height: size,
          fontSize: size * 0.4,
        }}
      />
    );
  }

  // Fallback to initials
  return (
    <Avatar
      alt={owner.name}
      sx={{
        width: size,
        height: size,
        fontSize: size * 0.4,
        bgcolor: getColorFromName(owner.name),
      }}
    >
      {getInitials(owner.name)}
    </Avatar>
  );
};

export default TaskAvatar;
